import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { 
  DollarSign, 
  Target, 
  Users, 
  Wallet, 
  ShoppingBag,
  Check,
  ArrowRight
} from 'lucide-react';

const UseCaseSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  const stepIcons = [DollarSign, Target, Users, Wallet, ShoppingBag];
  
  return (
    <section 
      id="use-case" 
      ref={ref}
      className="section-spacing relative bg-white"
      data-testid="use-case-section"
    >
      <div className="section-container relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase mb-4 block">
            {t.useCase.subtitle}
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A]">
            {t.useCase.title}
          </h2>
        </motion.div>
        
        <div className="max-w-5xl mx-auto">
          {/* Steps */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="grid md:grid-cols-5 gap-4 mb-12"
          >
            {t.useCase.steps.map((step, index) => {
              const Icon = stepIcons[index];
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="relative"
                >
                  <div className="card-light rounded-2xl p-5 text-center h-full hover:border-[#2563EB]/30 transition-colors">
                    {/* Step Number */}
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 bg-[#2563EB] rounded-full flex items-center justify-center text-white text-sm font-bold shadow-lg shadow-[#2563EB]/25">
                      {index + 1}
                    </div>
                    
                    {/* Icon */}
                    <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-[#2563EB]/10 flex items-center justify-center">
                      <Icon className="w-6 h-6 text-[#2563EB]" />
                    </div>
                    
                    {/* Text */}
                    <p className="text-sm text-[#475569] leading-relaxed">
                      {step}
                    </p>
                  </div>
                  
                  {/* Arrow */}
                  {index < 4 && (
                    <div className="hidden md:flex absolute top-1/2 -right-4 transform -translate-y-1/2 z-10">
                      <ArrowRight className="w-4 h-4 text-[#2563EB]/40" />
                    </div>
                  )}
                </motion.div>
              );
            })}
          </motion.div>
          
          {/* Results Card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="card-light rounded-3xl p-8 lg:p-10 border-[#2563EB]/20"
          >
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                <Check className="w-5 h-5 text-emerald-600" />
              </div>
              <h3 className="text-2xl font-bold text-[#0F172A]">{t.useCase.results.title}</h3>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Metrics */}
              <div className="space-y-4">
                {t.useCase.results.metrics.map((metric, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.7 + index * 0.1 }}
                    className="flex items-center gap-4"
                  >
                    <div className="w-2 h-2 rounded-full bg-[#2563EB]" />
                    <span className="text-lg text-[#0F172A] font-medium">{metric}</span>
                  </motion.div>
                ))}
              </div>
              
              {/* Benefits */}
              <div className="grid grid-cols-3 gap-4">
                {t.useCase.results.benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={isInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ delay: 0.9 + index * 0.1 }}
                    className="bg-emerald-50 rounded-xl p-4 text-center"
                  >
                    <div className="w-8 h-8 mx-auto mb-2 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Check className="w-4 h-4 text-emerald-600" />
                    </div>
                    <p className="text-sm text-emerald-700 font-medium">{benefit}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default UseCaseSection;
